/**
 * Persist Plot Grid outline offers and Use Notes (#1111, #2012).
 * Waiting drafts are keyed by book and scope: one sequence, or the whole
 * outline. The old story-level key is read once and moved onto its scope.
 */

export const WHOLE_OUTLINE_SCOPE = '__whole__';

export function pendingOfferKey(storyId) {
  return storyId ? `brainstormy.plottrack.offer.${storyId}` : '';
}

function pendingOffersKey(storyId) {
  return storyId ? `brainstormy.plottrack.offers.${storyId}` : '';
}

export function notesStorageKey(storyId) {
  return storyId ? `brainstormy.plottrack.useNotes.${storyId}` : '';
}

function readJson(key) {
  if (!key || typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeJson(key, value) {
  if (!key || typeof localStorage === 'undefined') return false;
  try {
    if (value == null) {
      localStorage.removeItem(key);
      return true;
    }
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    // Quota — offer stays on screen for this visit.
    return false;
  }
}

function collapseScopeText(value) {
  return String(value ?? '').replace(/\s+/g, ' ').trim();
}

/** One scope string for a sequence name, or the whole-outline scope. */
export function normalizeOfferScope(name) {
  const text = collapseScopeText(name);
  if (!text || text === WHOLE_OUTLINE_SCOPE) return WHOLE_OUTLINE_SCOPE;
  return text;
}

function metaScopeName(plan) {
  if (!plan || typeof plan !== 'object') return '';
  const meta = plan.meta && typeof plan.meta === 'object' && !Array.isArray(plan.meta)
    ? plan.meta
    : null;
  const candidates = [
    meta?.sequence,
    meta?.sequence_name,
    meta?.sequenceName,
    meta?.part_name,
    meta?.partName,
    plan.sequence_name,
    plan.sequenceName,
    plan.part_name,
    plan.partName,
  ];
  for (const value of candidates) {
    const text = collapseScopeText(value);
    if (text) return text;
  }
  return '';
}

function firstSequenceName(plan) {
  const acts = Array.isArray(plan?.acts) ? plan.acts : [];
  for (const act of acts) {
    if (!act || typeof act !== 'object') continue;
    const sequences = Array.isArray(act.sequences) ? act.sequences : [];
    const first = sequences[0];
    if (!first || typeof first !== 'object') continue;
    const name = collapseScopeText(first.name || first.title);
    if (name) return name;
  }
  return '';
}

/**
 * Scope for a run or a stored offer.
 * An explicit part name (the sequence being planned) wins.
 * whole: true is a whole-outline Generate, even when the plan names sequences.
 * Otherwise the plan's meta, then plan.acts[].sequences[0].name.
 */
export function offerScopeFor({ partName = '', plan = null, whole = false } = {}) {
  const named = collapseScopeText(partName);
  if (named && named !== WHOLE_OUTLINE_SCOPE) return named;
  if (whole || named === WHOLE_OUTLINE_SCOPE) return WHOLE_OUTLINE_SCOPE;
  return metaScopeName(plan) || firstSequenceName(plan) || WHOLE_OUTLINE_SCOPE;
}

export function offerScopeLabel(scope) {
  const key = normalizeOfferScope(scope);
  return key === WHOLE_OUTLINE_SCOPE ? 'Whole outline' : key;
}

function isPlanShape(plan) {
  if (!plan || typeof plan !== 'object' || Array.isArray(plan)) return false;
  if (Array.isArray(plan.acts) || Array.isArray(plan.parts)) return true;
  return Boolean(plan.meta && typeof plan.meta === 'object' && !Array.isArray(plan.meta));
}

function isOffer(stored) {
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return false;
  if (Array.isArray(stored.scenes)) return true;
  return isPlanShape(stored.plan);
}

function readScopeMap(key) {
  const stored = readJson(key);
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return {};
  if (isOffer(stored)) {
    return { [offerScopeFor({ plan: stored.plan })]: stored };
  }
  const map = {};
  Object.entries(stored).forEach(([scope, value]) => {
    if (!isOffer(value)) return;
    map[normalizeOfferScope(scope)] = value;
  });
  return map;
}

function loadMap(storyId) {
  if (!storyId) return {};
  const map = readScopeMap(pendingOffersKey(storyId));
  const legacy = readJson(pendingOfferKey(storyId));
  if (!isOffer(legacy)) return map;
  const scope = offerScopeFor({ plan: legacy.plan });
  if (!map[scope]) map[scope] = legacy;
  const saved = writeJson(pendingOffersKey(storyId), map);
  if (saved) writeJson(pendingOfferKey(storyId), null);
  return map;
}

export function readPendingOffers(storyId) {
  if (!storyId) return {};
  return { ...loadMap(storyId) };
}

export function readPendingOffer(storyId, scope) {
  if (!storyId) return null;
  const map = loadMap(storyId);
  if (arguments.length >= 2) {
    if (scope == null || collapseScopeText(scope) === '') return null;
    return map[normalizeOfferScope(scope)] || null;
  }
  const values = Object.values(map);
  if (values.length === 0) return null;
  return map[WHOLE_OUTLINE_SCOPE] || values[0];
}

export function writePendingOffer(storyId, offer, scope) {
  if (!storyId) return;
  const explicit = arguments.length >= 3;
  if (!offer) {
    if (explicit) clearPendingOffer(storyId, scope);
    else clearPendingOffer(storyId);
    return;
  }
  const map = loadMap(storyId);
  const key = explicit
    ? normalizeOfferScope(scope)
    : offerScopeFor({ plan: offer.plan });
  map[key] = offer;
  writeJson(pendingOffersKey(storyId), map);
  writeJson(pendingOfferKey(storyId), null);
}

export function clearPendingOffer(storyId, scope) {
  if (!storyId) return;
  if (arguments.length < 2) {
    writeJson(pendingOffersKey(storyId), null);
    writeJson(pendingOfferKey(storyId), null);
    return;
  }
  if (scope == null || collapseScopeText(scope) === '') return;
  const map = loadMap(storyId);
  delete map[normalizeOfferScope(scope)];
  if (Object.keys(map).length === 0) writeJson(pendingOffersKey(storyId), null);
  else writeJson(pendingOffersKey(storyId), map);
}

export function readUseNotes(storyId) {
  const stored = readJson(notesStorageKey(storyId));
  return stored === true;
}

export function writeUseNotes(storyId, value) {
  if (!storyId) return;
  writeJson(notesStorageKey(storyId), value === true);
}

export function clearUseNotes(storyId) {
  writeJson(notesStorageKey(storyId), null);
}
